type ClipboardPolicy = { allowsFeature(feature: string): boolean };
type ClipboardDocument = Document & {
  permissionsPolicy?: ClipboardPolicy;
  featurePolicy?: ClipboardPolicy;
};

function allowsClipboardWrite(): boolean {
  try {
    const doc = document as ClipboardDocument;
    const policy = doc.permissionsPolicy ?? doc.featurePolicy;
    return policy?.allowsFeature("clipboard-write") !== false;
  } catch {
    // The optional policy API may be unsupported. The write is authoritative.
    return true;
  }
}

function preserveSelection(): () => void {
  const active = document.activeElement as HTMLElement | null;
  let selection: Selection | null = null;
  const ranges: Range[] = [];
  try {
    selection = document.getSelection();
    for (let i = 0; selection && i < selection.rangeCount; i += 1)
      ranges.push(selection.getRangeAt(i).cloneRange());
  } catch {
    // A detached selection must not prevent copying.
  }
  const input =
    active?.tagName === "INPUT" || active?.tagName === "TEXTAREA"
      ? (active as HTMLInputElement | HTMLTextAreaElement)
      : null;
  let caret: [number, number, "forward" | "backward" | "none"] | null = null;
  try {
    if (input?.selectionStart != null && input.selectionEnd != null)
      caret = [
        input.selectionStart,
        input.selectionEnd,
        input.selectionDirection ?? "none",
      ];
  } catch {
    // Some input types do not expose a text selection.
  }

  return () => {
    try {
      if (active?.isConnected && typeof active.focus === "function")
        active.focus({ preventScroll: true });
    } catch {
      // Focus handlers can remove the previous control.
    }
    try {
      if (input?.isConnected && caret)
        input.setSelectionRange(caret[0], caret[1], caret[2]);
    } catch {
      // Restore the document selection even if this control has changed type.
    }
    try {
      selection?.removeAllRanges();
      for (const range of ranges) selection?.addRange(range);
    } catch {
      // Previously selected content may no longer be in the document.
    }
  };
}

function copyWithSelection(text: string): boolean {
  let field: HTMLTextAreaElement | undefined;
  const restore = preserveSelection();
  try {
    if (!document.body || typeof document.execCommand !== "function")
      return false;
    field = document.createElement("textarea");
    field.value = text;
    field.readOnly = true;
    field.tabIndex = -1;
    field.setAttribute("aria-hidden", "true");
    Object.assign(field.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "1px",
      height: "1px",
      padding: "0",
      border: "0",
      opacity: "0",
      pointerEvents: "none",
      fontSize: "16px",
    });
    // Keep the field inside a modal's active region rather than its inert backdrop.
    const host =
      document.activeElement?.closest(
        'dialog[open], [role="dialog"], [role="alertdialog"]',
      ) ?? document.body;
    host.appendChild(field);
    field.focus({ preventScroll: true });
    field.select();
    field.setSelectionRange(0, text.length);
    return document.execCommand("copy") === true;
  } catch {
    return false;
  } finally {
    try {
      field?.remove();
    } finally {
      restore();
    }
  }
}

/** Call directly from a user gesture; true means one copy method succeeded. */
export async function copyText(text: string): Promise<boolean> {
  if (!text || typeof window === "undefined" || typeof document === "undefined")
    return false;

  if (window.isSecureContext && allowsClipboardWrite()) {
    try {
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.clipboard?.writeText === "function"
      ) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // Permission, context or focus restrictions can reject async clipboard writes.
    }
  }
  // HTTP LAN pages and denied clipboard policies still get the selected-field path.
  return copyWithSelection(text);
}
